import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { makePostCallForInitialDataFetch } from "../apis/makePostCallForInitialDataFetch";
import { generateFilterPayloadForReviewPendingOrders } from "../utils/generateFilterPayloadForReviewPendingOrders";
import { recursivelyGeneratePayloadForInitialFetch } from "../utils/recursivelyGeneratePayloadForInitialFetch";
import { ReviewPendingOrdersState } from "./useReviewPendingOrdersTableData";
import { useValidFilters } from "./useValidFilters";

/**
 * Orchestrates the initial data fetch for the Review Pending Orders table.
 * Generates payloads for all pages up to the previously fetched batch (from table context),
 * then triggers the bulk fetch. Resets and re-fetches when the tableName or authStatus changes.
 *
 * @param setReviewPendingOrdersState - State setter for the orders loading/data/error state
 * @param setTableData - State setter for the transformed table row data
 * @param tableName - Table context key (supports auth-status-specific table names)
 * @param authStatus - Optional auth status segment to scope the initial payload
 * @returns initialDataFetchingExecuted flag and its setter for re-fetch control
 */
export const useInitialDataFetchingForTable = (
  setReviewPendingOrdersState: React.Dispatch<
    React.SetStateAction<ReviewPendingOrdersState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersTableData[]>
  >,
  tableName?: string,
  authStatus?: string,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const [initialDataFetchingExecuted, setInitialDataFetchingExecuted] =
    useState(false);

  const orgId = getOrgIdForFetchExternalWorklist();

  const { getTableDataFromContext } = useTablesContext();

  const tableAttributes = getTableDataFromContext(
    tableName ?? TableNames.REVIEW_PENDING_ORDERS_FROM_ALGOLIA,
  );

  const validFilters = useValidFilters();

  useEffect(() => {
    setTableData([]);
    setInitialDataFetchingExecuted(false);
  }, [tableName, authStatus]);

  useEffect(() => {
    if (
      !initialDataFetchingExecuted &&
      authStatusOptions &&
      authStatusOptions.length > 0
    ) {
      const payload = generateFilterPayloadForReviewPendingOrders(
        validFilters,
        tableAttributes?.searchedText ?? "",
        orgId,
        user?.id ?? "",
        authStatus,
        tableAttributes?.sortByField,
        tableAttributes?.sortAscending,
      );
      const payloads = recursivelyGeneratePayloadForInitialFetch(
        payload,
        tableAttributes?.lastBatchFetched ?? 0,
        tableAttributes?.sortByField ?? "",
        tableAttributes?.sortAscending ?? true,
      );

      makePostCallForInitialDataFetch(
        payloads,
        authStatusOptions,
        setReviewPendingOrdersState,
        setTableData,
        setInitialDataFetchingExecuted,
      );
    }
  }, [authStatusOptions, initialDataFetchingExecuted]);

  return {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
  };
};
